/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import autobind from 'class-autobind';
import LoadingIndicator from '../uiold/LoadingIndicator';
import loadImage from '../../support/helpers/loadImage';
import createTextComponent from '../../support/rn-web/createTextComponent';
import { getDOMSize } from '../../support/getImageSize';

type Props = {
  onClick: (event: Event) => void;
  src?: string | null | undefined;
  href?: string | null | undefined;
  width?: number;
  height?: number;
  onError?: () => void;
  showError?: string | null | undefined;
};

type State = {
  isLoading: boolean;
  loadingError: string | null | undefined;
  imageWidth: number;
  imageHeight: number;
};

const Link = createTextComponent({
  tagName: 'a',
  additionalProps: {
    className: true,
  },
});

export default class ThumbnailImage extends Component<Props, State> {
  _imageLoadSubscription: Subscription | null | undefined;

  constructor(props: Props) {
    super(props);
    autobind(this);
    this.state = {
      isLoading: true,
      loadingError: null,
      imageWidth: 0,
      imageHeight: 0,
    };
  }

  componentDidMount() {
    this._fetchImage(this.props.src);
  }

  componentWillUnmount() {
    if (this._imageLoadSubscription) {
      this._imageLoadSubscription.unsubscribe();
    }
  }

  componentDidUpdate(prevProps: Props) {
    let { src } = this.props;
    if (src !== prevProps.src) {
      this._fetchImage(src);
    }
  }

  render() {
    let { href, onClick } = this.props;
    if (href == null) {
      return (
        <div className="thumbnail-image" style={{ borderWidth: 0 }}>
          {this._renderThumbnail()}
        </div>
      );
    } else {
      return (
        <Link
          style={{ borderWidth: 0 }}
          className="thumbnail-image"
          href={href}
          onPress={onClick as any}
        >
          {this._renderThumbnail()}
        </Link>
      );
    }
  }

  _renderThumbnail() {
    let { showError, src, width, height } = this.props;
    let { isLoading, loadingError } = this.state;
    if (src == null) {
      isLoading = true;
    }
    let error = showError || loadingError;
    let style = {
      display: 'inline-block',
      width,
      height,
      backgroundImage: src ? `url("${src}")` : undefined,
      backgroundSize: 'contain',
      backgroundPositionX: 'center',
      backgroundPositionY: 'center',
      backgroundRepeat: 'no-repeat',
    };
    return (
      <span className="thumbnail-image-element" style={style}>
        {this._renderContent(error, isLoading)}
      </span>
    );
  }

  _renderContent(error: string | null | undefined, isLoading: boolean) {
    if (error) {
      return (
        <span className="no-image" title={error}>
          <span className="text">{t('No Preview Available')}</span>
          <span className="caption">{t('(click to download)')}</span>
        </span>
      );
    }
    if (isLoading) {
      return <LoadingIndicator />;
    }
  }

  _fetchImage(src: string | null | undefined) {
    if (src == null) {
      if (this._imageLoadSubscription) {
        this._imageLoadSubscription.unsubscribe();
        this._imageLoadSubscription = null;
      }
      this.setState({ isLoading: false });
      return;
    }
    this._imageLoadSubscription = loadImage(
      src,
      this._onImageLoaded,
      this._onImageLoadFailed,
    );
    this.setState({ isLoading: true });
  }

  _onImageLoaded(image: HTMLImageElement) {
    let { width, height } = image;
    // Workaround an issue where an SVG might have no fixed dimensions.
    if (width === 0 && height === 0) {
      ({ width, height } = getDOMSize(image, 1000, 1000));
    }
    this.setState({
      isLoading: false,
      imageWidth: width,
      imageHeight: height,
    });
  }

  _onImageLoadFailed() {
    let { onError } = this.props;
    this.setState({
      isLoading: false,
      loadingError: t('Loading Error'),
    });
    if (onError) {
      onError();
    }
  }
}
